import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Note,
  Trash,
  Phone,
  Bulb,
  Upload,
  Email,
  Whatsapp,
  LocationAlt,
  Settings,
  AlertInfo,
  Plus,
  Building,
  AlertTriangle,
  Checkmark,
  Edit,
} from "assets/icons";
import { ArrowLeft, GitMerge, Warning } from "@phosphor-icons/react";
import { Checkbox, Listbox, ListboxItem, Spacer, Spinner } from "@heroui/react";
import { CompanyType, ContactTags, Contact } from "shared/types/contacts.ts";
import { useEffect, useRef, useState, MouseEvent } from "react";
import * as Sentry from "@sentry/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import SearchComponent from "shared/components/searchBar/components/searchComponent";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { ResourceType } from "shared/types/resource";
import { useResources } from "shared/contexts/resources.context";
import InputSkeleton from "shared/skeletons/input.skeleton";
import {
  AddressPicker,
  Avatar,
  EditableInputComponent,
  EditableTextareaComponent,
  getMapsURL,
  renderSection,
  SectionHeader,
} from "components/index";
import DocumentsList from "components/documents/documentsList";
import TagSelector from "screens/contacts/components/tagSelector";
import FileUploadComponent from "components/documents/fileUpload";
import { EditableSelectComponent } from "components/editableInputs/editableSelect";
import SubMenu from "components/subMenu/subMenu";
import SubMenuCell from "components/subMenu/subMenuCell";
import ButtonNew from "components/buttonNew/buttonNew";
import { FieldType } from "shared/types/field";
import ReactDatePicker from "react-datepicker";

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import PhoneInput, { countryCodes } from "./components/phoneInput";
import { WhatsappLogo } from "@phosphor-icons/react";
import { ContactCallButton } from "./components/ContactCallButton";
import { CallLogsSection } from "./components/CallLogsSection";
import { ContactBuildingLinksSection } from "./components/ContactBuildingLinksSection";
import { ContactRentalInfoSection } from "./components/ContactRentalInfoSection";
import { IncassoDebtSection } from "shared/components/rental/components/IncassoDebtSection";
import { ContactEmailSection } from "./components/ContactEmailSection";
import { WhatsAppContactSection } from "shared/components/whatsapp/WhatsAppContactSection";

const ContactDetails = () => {
  const triggerRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const { session } = useSession();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editedContact, setEditedContact] = useState<Contact | null>(null);
  const { refreshResources } = useResources();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedTags, setSelectedTags] = useState<string[]>(
    contact?.tags || [],
  );
  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);
  const [vCardUrl, setVCardUrl] = useState<string | null>(null);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [createdCompany, setCreatedCompany] = useState<CompanyType | null>(
    null,
  );
  const [businessCardUploading, setBusinessCardUploading] = useState(false);
  const [hasAddressPlaceId, setHasAddressPlaceId] = useState<boolean>(false);
  const [isCreatingFromBusinessCard, setIsCreatingFromBusinessCard] =
    useState<boolean>(false);
  const [refetchDocuments, setRefetchDocuments] = useState({});
  const [refetchCallLogs, setRefetchCallLogs] = useState({});
  const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [selectedDuplicateCompany, setSelectedDuplicateCompany] =
    useState(null);
  const location = useLocation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeTargetContact, setMergeTargetContact] = useState<any>(null);
  const [mergeTargetDetails, setMergeTargetDetails] = useState<Contact | null>(
    null,
  );
  const [fieldSelections, setFieldSelections] = useState<{
    email?: "source" | "target";
    phone_mobile?: "source" | "target";
    phone_direct?: "source" | "target";
  }>({});
  const [keepCurrentContact, setKeepCurrentContact] = useState<boolean>(false); // false = keep target (other contact), true = keep current
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<{
    field: string;
    duplicates: Array<{
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      phone_mobile: string;
      matchedField: string;
    }>;
  } | null>(null);
  const [contactRelations, setContactRelations] = useState<{
    relations: {
      helpdeskTickets: Array<{ id: number; title: string; status: string }>;
      buildingContacts: Array<{ id: number; buildingId: string; type: string }>;
      residents: Array<{ id: number; unitId: string }>;
      scribbles: Array<{ id: number; title: string }>;
      contractContacts: Array<{ id: number; contractId: string }>;
    };
    canDelete: boolean;
    deleteBlockedReason: string | null;
  } | null>(null);
  const [mergeTargetRelations, setMergeTargetRelations] = useState<{
    relations: {
      helpdeskTickets: Array<{ id: number; title: string; status: string }>;
      buildingContacts: Array<{ id: number; buildingId: string; type: string }>;
      residents: Array<{ id: number; unitId: string }>;
      scribbles: Array<{ id: number; title: string }>;
      contractContacts: Array<{ id: number; contractId: string }>;
    };
    canDelete: boolean;
    deleteBlockedReason: string | null;
  } | null>(null);
  const [isLoadingRelations, setIsLoadingRelations] = useState(false);
  const [previewContact, setPreviewContact] = useState<Contact | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Fetch contact for preview modal
  const fetchContactForPreview = async (contactId: number) => {
    setIsLoadingPreview(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/${contactId}`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const data = await response.json();
        setPreviewContact(data);
        setIsPreviewModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching contact for preview:", error);
      toast.error("Kon contact niet laden");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Fetch contact relations for delete/merge safety checks
  const fetchContactRelations = async (contactId: number) => {
    setIsLoadingRelations(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/${contactId}/relations`,
        { method: "GET" },
        session,
      );
      if (response.ok) {
        const data = await response.json();
        setContactRelations(data);
      }
    } catch (error) {
      console.error("Error fetching contact relations:", error);
    } finally {
      setIsLoadingRelations(false);
    }
  };

  const selectDuplicateCompany = (company: any) => {
    setSelectedDuplicateCompany(company);
    setCreatingCompany(false);
  };

  useEffect(() => {
    if (contact) {
      setEditedContact({
        ...contact,
        contact_source_info: contact.contact_source_info || "",
        language: contact.language || "nl_BE",
      });
    }
  }, [contact]);

  // useEffect menu visibility
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Specifying the event type
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsSubMenuVisible(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside as unknown as EventListener,
    );
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside as unknown as EventListener,
      );
    };
  }, [menuRef]);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      // No ID => Creating a new contact
      const blankContact: Contact = {
        firstName: "",
        lastName: "",
        email: "",
        phone_mobile: "",
        jobTitle: "",
        gdpr_allowed: true,
        language: "nl_BE",
        contact_source_info: "",
      };

      setContact(blankContact);
      setEditedContact(blankContact);
      setEditMode(true); // Start in edit mode

      // Reset additional states for new contact creation
      setSelectedTags([]);
      setPotentialDuplicates([]);
      setSelectedDuplicateCompany(null);
      setCreatingCompany(false);
      setCreatedCompany(null);
      setBusinessCardUploading(false);
      setIsCreatingFromBusinessCard(false);
      setHasAddressPlaceId(false);
    }
  }, [id, location.search]);

  const fetchContact = async () => {
    if (id) {
      try {
        const data = await fetchWithAuth(
          `${API_URL}/contact/${id}`,
          { method: "GET" },
          session,
        );
        if (!data.ok) {
          throw new Error("Network response was not ok");
        }
        const c: any = await data.json();
        setContact(c);

        if (c?.company?.address?.placeId) setHasAddressPlaceId;
      } catch (error) {
        console.error("Failed to fetch contact: ", error);
        toast("Error bij laden van contact");
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (id) {
      // Reset edit mode and related states when navigating to a different contact
      setEditMode(false);
      setDuplicateWarning(null);
      setContactRelations(null);
      fetchContact();
    }
  }, [id]);

  const isValidContact = (contact: Contact) => {
    //  When creating a contact these fields should be at least present
    return (
      (contact?.firstName || contact?.lastName) &&
      (contact?.email || contact?.phone_mobile)
    );
  };

  useEffect(() => {
    if (editMode) {
      setSelectedTags(contact?.tags || []);
    }
  }, [contact, editMode, contact?.tags]);

  useEffect(() => {
    // Listener for Escape key to exit edit mode
    const handleKeydown = (e: KeyboardEvent) => {
      if (editMode && e.key === "Escape") {
        toggleEditMode();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => {
      // Cleanup event listener
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [contact, editMode]);

  const toggleNoteModal = () => {
    setIsNoteModalVisible(!isNoteModalVisible);
  };

  const handleBusinessCardUpload = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const file: File | undefined = e.target.files?.[0];
    if (!file) {
      toast.error("Kies een visitekaartje om te uploaden.");
      return;
    }

    const fileMeta = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || "unknown",
      lastModified: file.lastModified,
    };
    const lowerName = file.name.toLowerCase();
    const isHeic =
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      lowerName.endsWith(".heic") ||
      lowerName.endsWith(".heif");

    const formData = new FormData();
    formData.append("image", file);

    try {
      setBusinessCardUploading(true);
      setIsCreatingFromBusinessCard(true);

      const response = await fetchWithAuth(
        `${API_URL}/contact/create-from-business-card`,
        {
          method: "POST",
          body: formData,
        },
        session,
      );

      // Read body once as text so we can parse safely on both success and error paths.
      const rawBody = await response.text();
      let data: any = {};
      try {
        data = rawBody ? JSON.parse(rawBody) : {};
      } catch (parseError) {
        Sentry.captureException(parseError, {
          tags: {
            feature: "businessCardScanner",
            stage: "parseResponse",
          },
          extra: {
            ...fileMeta,
            isHeic,
            httpStatus: response.status,
            rawBodyPreview: rawBody?.slice(0, 500),
          },
        });
        throw new Error(
          "Onverwacht antwoord van de server bij het analyseren van het visitekaartje.",
        );
      }

      if (!response.ok) {
        const serverMessage =
          data?.message || data?.error || `HTTP ${response.status}`;
        const httpError = new Error(
          typeof serverMessage === "string"
            ? serverMessage
            : JSON.stringify(serverMessage),
        );
        Sentry.captureException(httpError, {
          tags: {
            feature: "businessCardScanner",
            stage: "httpError",
          },
          extra: {
            ...fileMeta,
            isHeic,
            httpStatus: response.status,
            serverPayload: data,
          },
        });
        throw httpError;
      }

      setPotentialDuplicates(data.potentialDuplicateCompanies);

      console.log(data);

      // Defensive: backend may still embed an error field in a 200 response.
      if (data.error) {
        throw new Error(data.error);
      }

      if (data.phone_mobile) {
        // Remove any non-digit characters except '+' to keep the initial plus if present
        let cleanNumber = data.phone_mobile.replace(/[^+\d]/g, "");

        // Define a variable to hold the final phone number
        let formattedNumber = "";

        // Check if the number has a country code in the form of 0032, 0031, +32, +31, etc.
        const matchedCountry = countryCodes.find((country) => {
          const codeWithoutPlus = country.code.slice(1); // Remove the '+' for matching
          // Check for "00", "+" or direct country code without zero
          return (
            cleanNumber.startsWith("00" + codeWithoutPlus) ||
            cleanNumber.startsWith(country.code) ||
            cleanNumber.startsWith(codeWithoutPlus)
          );
        });

        if (matchedCountry) {
          // If the number has a matched country code
          const codeWithoutPlus = matchedCountry.code.slice(1);

          if (cleanNumber.startsWith("00" + codeWithoutPlus)) {
            // Replace '0032' with '+32'
            formattedNumber = cleanNumber.replace(
              "00" + codeWithoutPlus,
              matchedCountry.code,
            );
          } else if (cleanNumber.startsWith(matchedCountry.code)) {
            // If it starts with '+32'
            formattedNumber = cleanNumber;
          } else if (cleanNumber.startsWith(codeWithoutPlus)) {
            // If it starts with '32', just prepend '+'
            formattedNumber = "+" + cleanNumber;
          }

          // Remove the optional (0) directly following the country code
          formattedNumber = formattedNumber.replace(
            new RegExp(`^\\${matchedCountry.code}\\(0\\)`),
            matchedCountry.code,
          );
          // Remove any leading zeros from the rest of the number
          formattedNumber = formattedNumber.replace(
            new RegExp(`^\\${matchedCountry.code}0+`),
            matchedCountry.code,
          );
        } else {
          // If no country code is detected, prepend the default country code (e.g., +32)
          // Remove any leading zeros from the local number
          formattedNumber = "+32" + cleanNumber.replace(/^0+/, "");
        }

        // Set the cleaned and formatted number back to data.phone_mobile
        data.phone_mobile = formattedNumber;
      }

      toast.success(
        "Visitekaartje geanalyseerd. Kijk goed na en vergeet niet op te slaan!",
      );
      setEditedContact({ ...data, gdpr_allowed: true, language: "nl_BE" });
      setCreatingCompany(true);
      setCreatedCompany(data.company);
    } catch (error: any) {
      console.error("Failed to upload file: ", error);
      Sentry.captureException(error, {
        tags: {
          feature: "businessCardScanner",
          stage: "upload",
          isHeic: String(isHeic),
        },
        extra: fileMeta,
      });
      const friendly = isHeic
        ? "HEIC-bestanden worden niet ondersteund. Converteer eerst naar JPEG of PNG en probeer opnieuw."
        : `Visitekaartje analyseren is mislukt: ${error?.message ?? "onbekende fout"}`;
      toast.error(friendly);
    } finally {
      setBusinessCardUploading(false);
    }
  };

  const handleNewCompanyDetailsChange = (field: any, value: any) => {
    setCreatedCompany((prev: CompanyType) => ({
      ...prev,
      [field]: value,
    }));
  };

  const generateVCard = () => {
    // Example vCard data
    let vCardData = `
      BEGIN:VCARD
      VERSION:3.0
      FN:${contact?.firstName} ${contact?.lastName}
      EMAIL:${contact?.email}
      TEL:${contact?.phone_mobile}
      END:VCARD`;

    // Convert to Blob
    const vCardBlob = new Blob([vCardData], { type: "text/vcard" });

    // Generate URL for Blob
    setVCardUrl(URL.createObjectURL(vCardBlob));
  };

  const createMeetingNotes = async (
    contact: any,
    saveLocation: "contact" | "company",
  ) => {
    setIsNoteModalVisible(false); // close modal

    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/generate-notes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contact: contact,
            locationType: saveLocation,
          }),
        },
        session,
      );

      const responseData = await response.json();

      // Show a toast message always
      toast(responseData.message);

      // If a copiedFileUrl is returned, open it in a new tab
      if (responseData.copiedFileUrl) {
        window.open(responseData.copiedFileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error creating notes:", error);
      toast("Error creating meeting notes!");
    }
  };

  const toggleEditMode = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedContact(contact); // initialize editedContact when entering edit mode
    }
  };

  const handleUploadSuccess = () => {
    // TODO: do something to refetch the component
    setRefetchDocuments({});
  };

  const handleTagClick = (tag: string) => {
    if (selectedTags.includes(tag)) {
      // Remove tag
      setSelectedTags((prevTags) => prevTags.filter((t) => t !== tag));
    } else {
      // Add tag
      setSelectedTags((prevTags) => [...prevTags, tag]);
    }
  };

  const handleCreatedCompanyAddressSelect = (selectedPlace: any) => {
    console.log("Selected address:", selectedPlace);
    setCreatedCompany((prev) => ({
      ...prev,
      address: {
        ...prev?.address,
        placeId: selectedPlace.placeId,
        formattedAddress: selectedPlace.description,
      },
    }));

    setHasAddressPlaceId(true);
  };

  const saveContactUpdates = async () => {
    if (!editedContact || !isValidContact(editedContact)) {
      toast.error(
        "Voor- en achternaam moeten ingevuld zijn en daarnaast minstens email of gsm nummer.",
      );
      return;
    }

    console.log(editedContact);

    if (
      !hasAddressPlaceId &&
      isCreatingFromBusinessCard &&
      !selectedDuplicateCompany
    ) {
      console.log("Currently disabled");
      // toast.error("Je moet het adres actief aanklikken in de adresbalk");
      // return;
    }

    const updatedContact = {
      ...editedContact,
      tags: selectedTags,
      company: selectedDuplicateCompany
        ? { id: (selectedDuplicateCompany as any).id }
        : editedContact.company
          ? editedContact?.company
          : contact?.company
            ? contact.company
            : null,
      created_company:
        (selectedDuplicateCompany ? null : createdCompany) || null,
    };

    try {
      let newContactData: Contact | null = null;
      if (id) {
        // Updating an existing contact
        await fetchWithAuth(
          `${API_URL}/contact/update-contact/${contact?.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedContact),
          },
          session,
        );
        fetchContact();
        toast("Contact bewaard!");

        // Navigate to the updated contact after 2 seconds
        setTimeout(() => {
          navigate(`/contact/${contact?.id}`);
        }, 2000);
      } else {
        // Creating a new contact
        const response = await fetchWithAuth(
          `${API_URL}/contact/create-contact`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedContact),
          },
          session,
        );
        newContactData = await response.json();
        if (newContactData && newContactData.id) {
          setContact(newContactData);
          setTimeout(() => {
            if (newContactData) {
              navigate(`/contact/${newContactData.id}`);
            }
          }, 2000);
        }
      }

      refreshResources();
    } catch (error) {
      console.error("Failed to save contact: ", error);
      toast("Error saving contact!");
    } finally {
      // These extra resets ensure that the business card state is cleared.
      setEditMode(false);
      setCreatingCompany(false);
      setCreatedCompany(null);
      setIsCreatingFromBusinessCard(false);
      setPotentialDuplicates([]);
      setSelectedDuplicateCompany(null);
    }
  };

  // Delete contact handler
  const handleDeleteContact = async () => {
    if (!contact?.id) return;

    try {
      setIsDeleting(true);
      const response = await fetchWithAuth(
        `${API_URL}/contact/delete-contact/${contact.id}`,
        { method: "DELETE" },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to delete contact");
      }

      toast.success("Contact verwijderd");
      refreshResources();

      // Navigate to contacts list after deletion
      setTimeout(() => {
        navigate("/contacts");
      }, 1500);
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error(
        "Er is een fout opgetreden bij het verwijderen van het contact",
      );
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  // Check for duplicates when editing fields
  const checkForDuplicates = async (
    fieldName: string,
    firstName?: string,
    lastName?: string,
    email?: string,
    phone?: string,
  ) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/check-duplicate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            phone,
            excludeId: contact?.id,
          }),
        },
        session,
      );

      if (response.ok) {
        const result = await response.json();
        if (result.hasDuplicate && result.duplicates.length > 0) {
          setDuplicateWarning({
            field: fieldName,
            duplicates: result.duplicates,
          });
        } else {
          setDuplicateWarning(null);
        }
      }
    } catch (error) {
      console.error("Error checking duplicates:", error);
    }
  };

  // Fetch target contact details when selected for merge
  const fetchMergeTargetDetails = async (targetId: string) => {
    try {
      // Fetch contact details and relations in parallel
      const [detailsResponse, relationsResponse] = await Promise.all([
        fetchWithAuth(
          `${API_URL}/contact/${targetId}`,
          { method: "GET" },
          session,
        ),
        fetchWithAuth(
          `${API_URL}/contact/${targetId}/relations`,
          { method: "GET" },
          session,
        ),
      ]);

      if (detailsResponse.ok) {
        const data = await detailsResponse.json();
        setMergeTargetDetails(data);
        // Reset field selections with defaults
        setFieldSelections({
          email: "target",
          phone_mobile: "target",
          phone_direct: "target",
        });
      }

      if (relationsResponse.ok) {
        const relationsData = await relationsResponse.json();
        setMergeTargetRelations(relationsData);
      }
    } catch (error) {
      console.error("Error fetching merge target details:", error);
    }
  };

  // Merge contacts handler
  const handleMergeContacts = async () => {
    if (!contact?.id || !mergeTargetContact?.resourceId) return;

    // Determine source (deleted) and target (kept) based on user choice
    const sourceId = keepCurrentContact
      ? parseInt(mergeTargetContact.resourceId)
      : contact.id;
    const targetId = keepCurrentContact
      ? contact.id
      : parseInt(mergeTargetContact.resourceId);

    // Swap field selections if keeping current contact
    const adjustedFieldSelections = keepCurrentContact
      ? {
          email: fieldSelections.email === "source" ? "target" : "source",
          phone_mobile:
            fieldSelections.phone_mobile === "source" ? "target" : "source",
          phone_direct:
            fieldSelections.phone_direct === "source" ? "target" : "source",
        }
      : fieldSelections;

    try {
      setIsMerging(true);
      const response = await fetchWithAuth(
        `${API_URL}/contact/merge`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sourceContactId: sourceId,
            targetContactId: targetId,
            fieldSelections: adjustedFieldSelections,
          }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to merge contacts");
      }

      const result = await response.json();
      const keptContactName = keepCurrentContact
        ? `${contact.firstName} ${contact.lastName}`
        : mergeTargetContact.name;
      const deletedContactName = keepCurrentContact
        ? mergeTargetContact.name
        : `${contact.firstName} ${contact.lastName}`;

      toast.success(
        `${deletedContactName} samengevoegd met ${keptContactName}. ${
          result.mergedRelations.helpdeskTickets > 0
            ? `${result.mergedRelations.helpdeskTickets} helpdesk tickets, `
            : ""
        }${
          result.mergedRelations.buildingContacts > 0
            ? `${result.mergedRelations.buildingContacts} gebouw koppelingen, `
            : ""
        }${
          result.mergedRelations.contractContacts > 0
            ? `${result.mergedRelations.contractContacts} contracten `
            : ""
        }overgezet.`,
      );
      refreshResources();

      // Navigate to the kept contact
      const keptContactId = keepCurrentContact
        ? contact.id
        : mergeTargetContact.resourceId;

      setTimeout(() => {
        if (keepCurrentContact) {
          // Refresh current page
          window.location.reload();
        } else {
          navigate(`/contact/${keptContactId}`);
        }
      }, 1500);
    } catch (error) {
      console.error("Error merging contacts:", error);
      toast.error(
        "Er is een fout opgetreden bij het samenvoegen van de contacten",
      );
    } finally {
      setIsMerging(false);
      setIsMergeModalOpen(false);
      setMergeTargetContact(null);
      setMergeTargetDetails(null);
      setMergeTargetRelations(null);
      setFieldSelections({});
      setKeepCurrentContact(false);
    }
  };

  const languageOptions = [
    { value: "en_US", label: "Engels" },
    { value: "nl_BE", label: "Nederlands" },
    { value: "fr_BE", label: "Frans" },
    // ... add other languages as needed
  ];

  const updateContactInfo = (updatedFields: any) => {
    console.log(updatedFields);
    setEditedContact((prev: any) => ({ ...prev, ...updatedFields }));
  };

  const nationalityOptions = [
    { value: "België", label: "België" },
    { value: "Netherlands", label: "Nederland" },
    { value: "Oekraïne", label: "Oekraïne" },
    { value: "Afghanistan", label: "Afghanistan" },
    { value: "Ethiopia", label: "Ethiopië" },
    { value: "Iraq", label: "Irak" },
    { value: "Congo", label: "Congo" },
    { value: "Turkey", label: "Turkije" },
    { value: "Greece", label: "Griekenland" },
    { value: "France", label: "Frankrijk" },
    { value: "Romania", label: "Roemenië" },
    { value: "Poland", label: "Polen" },
    { value: "Albania", label: "Albanië" },
    { value: "Armenia", label: "Armenië" },
    { value: "Iran", label: "Iran" },
    { value: "Germany", label: "Duitsland" },
    { value: "Italy", label: "Italië" },
    { value: "Eritrea", label: "Eritrea" },
    { value: "Slovakia", label: "Slowakije" },
    { value: "Bangladesh", label: "Bangladesh" },
    { value: "Syria", label: "Syrië" },
    { value: "Estonia", label: "Estland" },
    { value: "Spain", label: "Spanje" },
    { value: "Kyrgyzstan", label: "Kirgizië" },
    { value: "Morocco", label: "Marokko" },
    { value: "Finland", label: "Finland" },
    { value: "Colombia", label: "Colombia" },
    { value: "United States", label: "Verenigde Staten" },
    { value: "Portugal", label: "Portugal" },
  ];

  const handlePhoneChange = (newValue: string) => {
    setEditedContact((prev: any) => ({ ...prev, phone_mobile: newValue }));
  };

  const sharedContactProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: toggleEditMode,
    onSaveClick: saveContactUpdates,
    editedState: editedContact,
    originalState: contact,
    setStateFunction: setEditedContact,
  };

  // Inline duplicate warning component for specific fields
  const InlineDuplicateWarning = ({ fieldName }: { fieldName: string }) => {
    if (
      !duplicateWarning ||
      duplicateWarning.field !== fieldName ||
      duplicateWarning.duplicates.length === 0
    ) {
      return null;
    }

    return (
      <div className="mx-2.5 mb-2 rounded-md border border-amber-300 bg-amber-50 p-2">
        <div className="flex items-start gap-2">
          <Warning size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="flex-1">
            <p className="text-xs font-medium text-amber-800">
              Er bestaat al een contact met dit {fieldName}
            </p>
            <div className="mt-1 space-y-1">
              {duplicateWarning.duplicates.slice(0, 2).map((dup) => (
                <div
                  key={dup.id}
                  className="flex items-center justify-between text-xs"
                >
                  <span className="text-neutral-700">
                    {dup.firstName} {dup.lastName}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => fetchContactForPreview(dup.id)}
                      disabled={isLoadingPreview}
                      className="rounded bg-neutral-100 px-2 py-0.5 text-neutral-600 hover:bg-neutral-200 disabled:opacity-50"
                    >
                      {isLoadingPreview ? "..." : "Bekijken"}
                    </button>
                    <button
                      onClick={() => {
                        setMergeTargetContact({
                          resourceId: dup.id.toString(),
                          name: `${dup.firstName} ${dup.lastName}`,
                          metadata1: dup.phone_mobile,
                        });
                        fetchMergeTargetDetails(dup.id.toString());
                        setIsMergeModalOpen(true);
                        setDuplicateWarning(null);
                      }}
                      className="bg-moss-primary hover:bg-moss-primary/90 flex items-center gap-0.5 rounded px-2 py-0.5 text-white"
                    >
                      <GitMerge size={12} />
                      Merge
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setDuplicateWarning(null)}
              className="mt-1 text-xs text-amber-700 underline hover:text-amber-800"
            >
              Negeren
            </button>
          </div>
        </div>
      </div>
    );
  };

  const contactFieldsConfig = [
    {
      label: "Voornaam",
      key: "firstName",
      onBlur: () => {
        if (editedContact?.firstName && editedContact?.lastName) {
          checkForDuplicates(
            "naam",
            editedContact.firstName,
            editedContact.lastName,
            undefined,
            undefined,
          );
        }
      },
    },
    {
      label: "Achternaam",
      key: "lastName",
      onBlur: () => {
        if (editedContact?.firstName && editedContact?.lastName) {
          checkForDuplicates(
            "naam",
            editedContact.firstName,
            editedContact.lastName,
            undefined,
            undefined,
          );
        }
      },
      WarningComponent: () => <InlineDuplicateWarning fieldName="naam" />,
    },
    {
      label: "Bedrijf",
      key: "company",
      Component: () => {
        if (editMode) {
          return (
            <>
              {!creatingCompany && (
                <div className="flex w-full flex-col gap-4">
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="grow">
                      <SearchComponent
                        type="regular"
                        label="Zoek bedrijf"
                        resourceFilter={[ResourceType.COMPANY]}
                        initialValue={
                          contact?.company?.name || editedContact?.company?.name
                        }
                        onSelect={(resource) =>
                          setEditedContact((prev: any) => ({
                            ...prev,
                            company: {
                              id: resource.resourceId,
                              name: resource.name,
                            },
                          }))
                        }
                      />
                    </div>
                    <ButtonNew
                      variant="primaryMoss"
                      onClick={() => navigate("/company/new")}
                      icon={<Plus />}
                      label="Nieuw bedrijf toevoegen"
                    />
                  </div>
                </div>
              )}
            </>
          );
        } else if (contact?.company) {
          return (
            <Link to={`/company/${contact?.company?.id}`}>
              <p className="text-moss-primary font-medium">
                {contact?.company?.name}
              </p>
            </Link>
          );
        } else {
          return <p className="font-medium">Niet ingevuld</p>;
        }
      },
    },
    { label: "Functie", key: "jobTitle" },
    {
      label: "E-mail",
      key: "email",
      type: FieldType.EMAIL,
      onBlur: () => {
        if (editedContact?.email) {
          checkForDuplicates(
            "email",
            undefined,
            undefined,
            editedContact.email,
            undefined,
          );
        }
      },
      WarningComponent: () => <InlineDuplicateWarning fieldName="email" />,
    },
    {
      label: "Taal",
      key: "language",
      Component: () => (
        <EditableSelectComponent
          label="Selecteer taal"
          editMode={editMode}
          value={editedContact?.language || "nl_BE"}
          options={languageOptions}
          onChange={(value) =>
            setEditedContact((prev: any) => ({ ...prev, language: value }))
          }
        />
      ),
    },
    {
      label: "Geslacht",
      key: "gender",
      Component: () => (
        <EditableSelectComponent
          label="Geslacht"
          editMode={editMode}
          value={editedContact?.gender || ""}
          options={[
            { value: "M", label: "Man" },
            { value: "F", label: "Vrouw" },
            { value: "X", label: "X" },
          ]}
          onChange={(value) =>
            setEditedContact((prev: any) => ({ ...prev, gender: value }))
          }
        />
      ),
    },
    {
      label: "Privé adres",
      key: "address",
      Component: () => {
        // Render the AddressPicker in edit mode or a text representation of the address otherwise
        return (
          <div className={editMode ? "grow" : ""}>
            <AddressPicker
              placeholder="Privé adres persoon"
              editMode={editMode}
              defaultAddress={
                editedContact?.address?.formattedAddress ||
                contact?.address?.formattedAddress ||
                ""
              }
              getAddress={(selectedPlace) =>
                setEditedContact((prev: any) => ({
                  ...prev,
                  address: {
                    ...prev.address,
                    placeId: selectedPlace.placeId,
                    formattedAddress: selectedPlace.description,
                  },
                }))
              }
            />
          </div>
        );
      },
    },
    { label: "Beschrijving", key: "description" },
  ];

  const contactFieldsExtraConfig = [
    { label: "Odoo ID", key: "odoo_id", disableEditMode: true },
    { label: "Geboorteplaats", key: "birthplace" },
    { label: "Rijksregisternummer", key: "ssid" },
    {
      label: "Nationaliteit",
      key: "nationality",
      Component: () => (
        <EditableSelectComponent
          label="Selecteer nationaliteit"
          editMode={editMode}
          value={editedContact?.nationality || ""}
          options={nationalityOptions}
          onChange={(value) =>
            setEditedContact((prev: any) => ({
              ...prev,
              nationality: value,
            }))
          }
        />
      ),
    },
    { label: "LinkedIn", key: "linkedIn" },
    {
      label: "Tags",
      key: "tags",
      Component: () => {
        if (editMode) {
          return (
            <div className="flex flex-col gap-2">
              <p className="text-sm">Tags</p>
              <TagSelector
                tags={ContactTags}
                selectedTags={selectedTags}
                handleTagClick={handleTagClick}
              />
            </div>
          );
        } else if (contact?.tags && contact.tags.length > 0) {
          return (
            <div className="flex flex-wrap gap-2">
              {contact.tags.sort().map((tag, index) => (
                <span
                  key={index}
                  className="border-moss-primary text-moss-primary h-fit min-w-fit rounded-full border bg-white px-2 font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          );
        } else {
          return <p className="font-medium">Niet ingevuld</p>;
        }
      },
    },
    {
      label: "GDPR toestemming",
      key: "gdpr_allowed",
      Component: () => (
        <Checkbox
          isDisabled={editMode ? false : true}
          isSelected={editedContact?.gdpr_allowed || false}
          color="warning"
          onChange={(e) =>
            setEditedContact((prev: any) => ({
              ...prev,
              gdpr_allowed: e.target.checked,
            }))
          }
        >
          GDPR toestemming
        </Checkbox>
      ),
    },
    {
      label: "Waar en door wie werd dit contact ontmoet?",
      key: "contact_source_info",
      Component: EditableTextareaComponent,
    },
  ];

  return (
    <div className="z-10 mb-10">
      {!isLoading ? (
        <div className="flex flex-col items-center gap-2.5 sm:grow sm:flex-row">
          <Avatar src={contact?.picture ?? undefined} />
          <div className="flex flex-col items-center gap-0 sm:grow sm:items-start">
            <p className="text-3xl font-medium">
              {contact?.firstName} {contact?.lastName}
            </p>
            {contact?.company?.name && (
              <Link
                to={`/company/${contact?.company?.id}`}
                className="text-neutral-secondary-mid text-lg"
              >
                {contact?.company?.name}
              </Link>
              )}
          </div>
          <ButtonNew
            variant="secondaryMoss"
            onClick={() => navigate(`/contacts${location.search}`)}
            icon={<ArrowLeft size={16} />}
            label="Terug naar contacten"
            className="self-start sm:self-auto"
          />
          <ul className="flex flex-row items-center justify-center gap-2.5">
            {contact?.phone_mobile && contact?.id && (
              <li className="text-moss-primary flex items-center justify-center rounded-full bg-white p-2 shadow-md">
                <ContactCallButton
                  contactId={contact.id}
                  contactName={`${contact.firstName || ""} ${contact.lastName || ""}`.trim()}
                  phoneNumber={contact.phone_mobile}
                  size={20}
                  onCallLogged={() => setRefetchCallLogs({})}
                />
              </li>
            )}
            {contact?.phone_mobile && (
              <li>
                <a
                  href={"whatsapp://" + contact?.phone_mobile}
                  className="text-moss-primary flex items-center justify-center rounded-full bg-white p-2 shadow-md"
                >
                  <Whatsapp />
                </a>
              </li>
            )}
            {contact?.email && (
              <li>
                <a
                  href={"mailto:" + contact?.email}
                  className="text-moss-primary flex items-center justify-center rounded-full bg-white p-2 shadow-md"
                >
                  <Email />
                </a>
              </li>
            )}
            {contact?.address?.formattedAddress && (
              <li>
                <a
                  href={getMapsURL(contact.address.formattedAddress)}
                  className="text-moss-primary flex items-center justify-center rounded-full bg-white p-2 shadow-md"
                  target="_blank" // Opens in a new tab
                  rel="noopener noreferrer" // For security reasons
                >
                  <LocationAlt />
                </a>
              </li>
            )}
            {/*
            <li ref={triggerRef}>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  toggleSubMenu();
                }}
                className="flex items-center justify-center rounded-full bg-transparent p-2 text-moss-primary shadow-[inset_0_0_0_1px_rgba(49,64,59,0.25)]"
              >
                <More />
              </a>
            </li>
            */}
          </ul>
          {editMode && (
            <ButtonNew
              variant="primaryMoss"
              onClick={saveContactUpdates}
              icon={<Checkmark />}
              label="Bewaar contact"
              className="ml-auto"
            />
          )}
          {!editMode && id && (
            <div className="ml-auto flex items-center gap-2">
              <ButtonNew
                variant="secondaryMoss"
                onClick={() => setEditMode(true)}
                icon={<Edit />}
                label="Bewerken"
              />
              <ButtonNew
                variant="secondaryMoss"
                onClick={() => {
                  setIsMergeModalOpen(true);
                  if (contact?.id) {
                    fetchContactRelations(contact.id);
                  }
                }}
                icon={<GitMerge size={16} />}
                label="Samenvoegen"
              />
              <ButtonNew
                variant="secondaryBrick"
                onClick={() => {
                  setIsDeleteModalOpen(true);
                  if (contact?.id) {
                    fetchContactRelations(contact.id);
                  }
                }}
                icon={<Trash />}
                label="Verwijderen"
              />
            </div>
          )}
          {isSubMenuVisible && (
            <div ref={menuRef}>
              <SubMenu triggerRef={triggerRef}>
                <div>
                  <SubMenuCell
                    onClick={toggleNoteModal}
                    key="Nieuwe meeting notes"
                    label="Nieuwe meeting notes"
                    icon={<Note />}
                  />
                  {isNoteModalVisible && (
                    <Listbox
                      className="absolute z-10 bg-white shadow-md"
                      variant="bordered"
                      color="warning"
                      aria-label="Meeting notes"
                    >
                      <ListboxItem
                        key="company"
                        title={`Opslaan bij ${contact?.company?.name}`}
                        onClick={() =>
                          createMeetingNotes(contact?.company, "company")
                        }
                      />
                      <ListboxItem
                        key="contact"
                        title={`Opslaan bij ${contact?.firstName}`}
                        onClick={() => createMeetingNotes(contact, "contact")}
                      />
                    </Listbox>
                  )}
                </div>
                <SubMenuCell
                  onClick={() => console.log("Hello world")}
                  key="Stuur e-mail met Ella"
                  label="Stuur e-mail met Ella"
                  icon={<Bulb />}
                />
                <SubMenuCell
                  onClick={generateVCard}
                  key="Sync met telefoon"
                  label="Sync met telefoon"
                  icon={<Phone />}
                />
                <SubMenuCell
                  onClick={() => console.log("Hello world")}
                  key="Verwijder contact"
                  label="Verwijder contact"
                  icon={<Trash />}
                  className="bg-status-medium text-status-bad flex cursor-pointer items-center gap-2"
                />
              </SubMenu>
            </div>
          )}
        </div>
      ) : (
        <InputSkeleton />
      )}
      <Spacer y={10} />
      <div className="sm:rounded-lg sm:bg-white sm:p-5 sm:shadow-md">
        <div className="bg-status-medium flex justify-end gap-2">
          {vCardUrl && (
            <a
              href={vCardUrl}
              download={`${contact?.firstName}_${contact?.lastName}.vcf`}
            >
              Download vCard
            </a>
          )}
        </div>
        {/* Display Business Card Upload if No Contact Info Available */}
        {!id && (
          <>
            <div className="border-brick-primary flex flex-col items-center gap-2 rounded-md border-2 border-dashed p-2">
              <label
                htmlFor="businessCardSelector"
                className="bg-neutral-primary cursor-pointer rounded-full px-4 py-2 text-white"
              >
                <div className="flex items-center gap-2">
                  <div>
                    <Upload />
                  </div>
                  <p>
                    Je kan ook een foto van een visitekaartje uploaden om een
                    nieuw contact aan te maken
                  </p>
                </div>
              </label>
              <input
                key={`file-upload-${location.search}`}
                type="file"
                id="businessCardSelector"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,image/bmp,image/tiff,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.bmp,.tiff"
                style={{ display: "none" }}
                onChange={handleBusinessCardUpload}
                disabled={businessCardUploading}
              />
              {businessCardUploading && (
                <Spinner label="Aan het analyseren.." color="warning" />
              )}
            </div>
            <Spacer y={8} />
          </>
        )}
        {/* Content */}
        {selectedDuplicateCompany && (
          <div className="mt-4 rounded-lg bg-white p-4 shadow-md">
            <h3 className="text-lg font-medium">Geselecteerd Bedrijf:</h3>
            <div className="mt-2 flex items-center">
              <span className="text-neutral-secondary-mid mr-2">
                {(selectedDuplicateCompany as any).name}
              </span>
            </div>
          </div>
        )}

        {creatingCompany && (
          <div className="mb-4 flex w-full flex-col gap-3 border-b pb-3">
            {potentialDuplicates.length > 0 && (
              <div className="border-status-bad mb-4 rounded-lg border bg-white p-4 shadow-md">
                <h3 className="flex items-center gap-2 text-lg font-medium">
                  <AlertTriangle className="text-status-bad" />
                  Mogelijke duplicaten
                </h3>
                <p className="mt-1 text-xs text-neutral-secondary-mid">
                  Gezocht op de ingevoerde bedrijfsnaam. Controleer hieronder
                  waarom elk resultaat wordt voorgesteld voor je een bestaand
                  bedrijf kiest.
                </p>
                <ul className="list-none">
                  {potentialDuplicates.map((company: any) => (
                    <li
                      key={company.id}
                      className="flex items-center justify-between"
                    >
                      <span className="text-neutral-primary flex flex-col gap-0.5">
                        <span className="flex items-center gap-2">
                          <Building className="h-3.5 w-3.5" />
                          {company.name}
                        </span>
                        {company.matchReason && (
                          <span className="text-neutral-secondary-mid pl-5 text-xs">
                            {company.matchReason}
                          </span>
                        )}
                      </span>
                      <ButtonNew
                        variant="secondaryNeutral"
                        icon={<Checkmark />}
                        label="Kies dit bedrijf"
                        onClick={() => selectDuplicateCompany(company)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <p className="flex items-center gap-1 text-lg">
                <Building className="h-[18px] w-[18px]" />
                Nieuw bedrijf aanmaken
              </p>
              <div className="flex items-start gap-2 rounded-md border border-moss-primary/30 bg-moss-primary/10 p-3 text-sm text-neutral-primary">
                <AlertInfo className="mt-0.5 h-4 w-4 shrink-0 text-moss-primary" />
                <p>
                  Vul hieronder de bedrijfsgegevens in. Bij het klikken op{" "}
                  <span className="font-medium">"Bewaar contact"</span>{" "}
                  bovenaan wordt dit nieuwe bedrijf automatisch aangemaakt en
                  aan dit contact gekoppeld — er is geen aparte knop om het
                  bedrijf eerst op te slaan.
                </p>
              </div>
            </div>
            <EditableInputComponent
              editMode={creatingCompany}
              value={createdCompany?.name || ""}
              label="Bedrijfsnaam"
              isLoading={false}
              onChange={(value) => handleNewCompanyDetailsChange("name", value)}
            />
            <EditableInputComponent
              editMode={creatingCompany}
              value={createdCompany?.vatNumber || ""}
              label="VAT / KBO"
              isLoading={false}
              onChange={(value) =>
                handleNewCompanyDetailsChange("vatNumber", value)
              }
            />
            {!(
              editedContact?.address && editedContact?.address?.description
            ) ? (
              <AddressPicker
                editMode={editMode}
                placeholder="Bedrijfsadres"
                defaultAddress={createdCompany?.address?.formattedAddress || ""}
                getAddress={(selectedPlace) =>
                  handleCreatedCompanyAddressSelect(selectedPlace)
                }
                autoSelect={true}
              />
            ) : (
              <p>
                {editedContact?.address && editedContact?.address?.description}
              </p>
            )}
            <EditableInputComponent
              editMode={creatingCompany}
              value={createdCompany?.website || ""}
              label="Website"
              isLoading={false}
              onChange={(value) =>
                handleNewCompanyDetailsChange("website", value)
              }
            />
            <EditableInputComponent
              editMode={creatingCompany}
              value={createdCompany?.phone_direct || ""}
              label="Telefoon (vaste lijn)"
              isLoading={false}
              onChange={(value) =>
                handleNewCompanyDetailsChange("phone_direct", value)
              }
            />
          </div>
        )}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <div
              key={`phone-input-container-${location.search}`}
              className="divide-neutral-primary/15 border-neutral-primary/15 mb-4 divide-y rounded-md border bg-white"
            >
              <SectionHeader
                title={"Whatsapp contactgegevens"}
                icon={<WhatsappLogo />}
                editMode={editMode}
                onEditClick={toggleEditMode}
                onSaveClick={saveContactUpdates}
                showEditToggle={true}
              />
              <PhoneInput
                key={`phone-input-${location.search}`}
                value={
                  editedContact?.phone_mobile || contact?.phone_mobile || ""
                }
                onChange={handlePhoneChange}
                onBlur={() => {
                  const phone =
                    editedContact?.phone_mobile || contact?.phone_mobile;
                  if (phone) {
                    checkForDuplicates(
                      "telefoon",
                      undefined,
                      undefined,
                      undefined,
                      phone,
                    );
                  }
                }}
                editMode={editMode}
              />
              <InlineDuplicateWarning fieldName="telefoon" />
            </div>
            {renderSection({
              title: "Contactgegevens",
              icon: <AlertInfo />,
              fieldsConfig: contactFieldsConfig,
              ...sharedContactProps,
            })}
          </div>
          <div>
            {renderSection({
              title: "Uitgebreide gegevens",
              icon: <Settings />,
              fieldsConfig: contactFieldsExtraConfig,
              ...sharedContactProps,
            })}
            {editMode ? (
              <div className="mt-4 flex flex-col gap-2">
                <p className="text-sm">Geboortedatum</p>
                <DatePicker
                  selected={
                    editedContact?.birthdate
                      ? new Date(editedContact?.birthdate)
                      : null
                  }
                  onChange={(date: any) =>
                    updateContactInfo({ birthdate: date })
                  }
                  dateFormat="yyyy/MM/dd"
                  className="text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-opacity-50 z-10 w-full rounded-sm bg-white p-2.5 focus:ring-3"
                />
              </div>
            ) : (
              <div className="divide-neutral-primary/15 border-neutral-primary/15 divide-y rounded-md border bg-white">
                <div className="flex items-center justify-between p-2.5 text-sm">
                  <p className="text-sm">Geboortedatum</p>
                  {editedContact?.birthdate
                    ? editedContact?.birthdate?.toString()
                    : "Niet ingevuld"}
                </div>
              </div>
            )}
          </div>
          {/* Unit and building rental context */}
          {contact?.id && (
            <div className="sm:col-span-2">
              <ContactRentalInfoSection contactId={contact.id} />
            </div>
          )}

          {/* Gebouw Koppelingen Section */}
          {contact?.id && (
            <div className="sm:col-span-2">
              <ContactBuildingLinksSection contactId={contact.id} />
            </div>
          )}

          {/* Incasso Debt Section */}
          {contact?.id && (
            <div className="sm:col-span-2">
              <IncassoDebtSection contactId={contact.id} />
            </div>
          )}

          {/* Call Logs Section */}
          {contact?.id && (
            <div className="sm:col-span-2">
              <CallLogsSection
                contactId={contact.id}
                refreshTrigger={refetchCallLogs}
              />
            </div>
          )}

          {/* Email Communicatie Section */}
          {contact?.id && (
            <div className="sm:col-span-2">
              <div className="border-neutral-primary/15 rounded-md border bg-white p-4">
                <ContactEmailSection
                  contactId={contact.id}
                  contactEmail={contact.email}
                />
              </div>
            </div>
          )}

          {/* WhatsApp Communicatie Section — full conversation */}
          {contact?.id && (
            <div className="sm:col-span-2">
              <WhatsAppContactSection
                contactId={contact.id}
                contactName={
                  `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim() ||
                  "deze contactpersoon"
                }
                hasMobile={!!contact.phone_mobile}
                daysFilter={30}
                maxMessages={5}
              />
            </div>
          )}

          {/* Documents Section */}
          <div className="sm:col-span-2">
            <div className="border-neutral-primary/15 flex flex-col gap-2 rounded-md border bg-white pb-3">
              <DocumentsList
                refetchDocuments={refetchDocuments}
                sharePointFolderId={contact?.sharePointFolderId as string}
                sharePointFolderUrl={contact?.sharePointFolderUrl as string}
              />
              <FileUploadComponent
                resourceType={ResourceType.CONTACT}
                resource={contact}
                onUploadSuccess={handleUploadSuccess}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full ${contactRelations?.canDelete ? "bg-brick-primary/10" : "bg-amber-100"}`}
              >
                <Warning
                  size={24}
                  className={
                    contactRelations?.canDelete
                      ? "text-brick-primary"
                      : "text-amber-600"
                  }
                />
              </div>
              <div>
                <h3 className="text-neutral-primary text-lg font-semibold">
                  Contact verwijderen
                </h3>
                <p className="text-sm text-neutral-600">
                  {contactRelations?.canDelete
                    ? "Deze actie kan niet ongedaan worden gemaakt"
                    : "Dit contact kan niet verwijderd worden"}
                </p>
              </div>
            </div>

            {isLoadingRelations ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : contactRelations ? (
              <>
                {/* Show blocking reason */}
                {!contactRelations.canDelete &&
                  contactRelations.deleteBlockedReason && (
                    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
                      <p className="text-sm text-amber-800">
                        {contactRelations.deleteBlockedReason}
                      </p>
                    </div>
                  )}

                {/* Show related records */}
                {(contactRelations.relations.helpdeskTickets.length > 0 ||
                  contactRelations.relations.contractContacts.length > 0 ||
                  contactRelations.relations.buildingContacts.length > 0 ||
                  contactRelations.relations.residents.length > 0 ||
                  contactRelations.relations.scribbles.length > 0) && (
                  <div className="mb-4">
                    <p className="mb-2 text-sm font-medium text-neutral-700">
                      Gekoppelde gegevens:
                    </p>
                    <div className="max-h-60 space-y-2 overflow-y-auto">
                      {contactRelations.relations.helpdeskTickets.length >
                        0 && (
                        <div className="rounded-lg border border-neutral-200 p-3">
                          <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                            Helpdesk Tickets (
                            {contactRelations.relations.helpdeskTickets.length})
                          </p>
                          <div className="space-y-1">
                            {contactRelations.relations.helpdeskTickets
                              .slice(0, 5)
                              .map((ticket) => (
                                <div
                                  key={ticket.id}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span className="text-neutral-700">
                                    #{ticket.id} - {ticket.title}
                                  </span>
                                  <span className="text-xs text-neutral-500">
                                    {ticket.status}
                                  </span>
                                </div>
                              ))}
                            {contactRelations.relations.helpdeskTickets.length >
                              5 && (
                              <p className="text-xs text-neutral-500">
                                ... en{" "}
                                {contactRelations.relations.helpdeskTickets
                                  .length - 5}{" "}
                                meer
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {contactRelations.relations.contractContacts.length >
                        0 && (
                        <div className="rounded-lg border border-neutral-200 p-3">
                          <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                            Contracten (
                            {contactRelations.relations.contractContacts.length}
                            )
                          </p>
                          <div className="space-y-1">
                            {contactRelations.relations.contractContacts
                              .slice(0, 5)
                              .map((contract) => (
                                <div
                                  key={contract.id}
                                  className="text-sm text-neutral-700"
                                >
                                  Contract #{contract.contractId}
                                </div>
                              ))}
                            {contactRelations.relations.contractContacts
                              .length > 5 && (
                              <p className="text-xs text-neutral-500">
                                ... en{" "}
                                {contactRelations.relations.contractContacts
                                  .length - 5}{" "}
                                meer
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {contactRelations.relations.buildingContacts.length >
                        0 && (
                        <div className="rounded-lg border border-neutral-200 p-3">
                          <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                            Gebouw koppelingen (
                            {contactRelations.relations.buildingContacts.length}
                            )
                          </p>
                          <div className="space-y-1">
                            {contactRelations.relations.buildingContacts
                              .slice(0, 5)
                              .map((bc) => (
                                <div
                                  key={bc.id}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span className="text-neutral-700">
                                    {bc.buildingId}
                                  </span>
                                  <span className="text-xs text-neutral-500">
                                    {bc.type}
                                  </span>
                                </div>
                              ))}
                            {contactRelations.relations.buildingContacts
                              .length > 5 && (
                              <p className="text-xs text-neutral-500">
                                ... en{" "}
                                {contactRelations.relations.buildingContacts
                                  .length - 5}{" "}
                                meer
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {contactRelations.relations.residents.length > 0 && (
                        <div className="rounded-lg border border-neutral-200 p-3">
                          <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                            Bewoner koppelingen (
                            {contactRelations.relations.residents.length})
                          </p>
                        </div>
                      )}

                      {contactRelations.relations.scribbles.length > 0 && (
                        <div className="rounded-lg border border-neutral-200 p-3">
                          <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                            Notities (
                            {contactRelations.relations.scribbles.length})
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Confirmation text for deletable contacts */}
                {contactRelations.canDelete && (
                  <div className="mb-6 rounded-lg bg-neutral-50 p-4">
                    <p className="text-sm text-neutral-700">
                      Weet je zeker dat je{" "}
                      <strong>
                        {contact?.firstName} {contact?.lastName}
                      </strong>{" "}
                      wilt verwijderen?
                    </p>
                  </div>
                )}
              </>
            ) : null}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setContactRelations(null);
                }}
                className="rounded-lg border border-neutral-200 px-4 py-2 text-neutral-600 transition-colors hover:bg-neutral-50"
                disabled={isDeleting}
              >
                {contactRelations?.canDelete ? "Annuleren" : "Sluiten"}
              </button>
              {!contactRelations?.canDelete && (
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setContactRelations(null);
                    setIsMergeModalOpen(true);
                    if (contact?.id) {
                      fetchContactRelations(contact.id);
                    }
                  }}
                  className="bg-moss-primary hover:bg-moss-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-white transition-colors"
                >
                  <GitMerge size={16} />
                  Samenvoegen
                </button>
              )}
              {contactRelations?.canDelete && (
                <button
                  onClick={handleDeleteContact}
                  disabled={isDeleting || isLoadingRelations}
                  className="bg-brick-primary hover:bg-brick-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-white transition-colors disabled:opacity-50"
                >
                  {isDeleting ? <Spinner size="sm" color="white" /> : <Trash />}
                  {isDeleting ? "Verwijderen..." : "Verwijderen"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Merge Contact Modal */}
      {isMergeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-moss-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
                <GitMerge size={24} className="text-moss-primary" />
              </div>
              <div>
                <h3 className="text-neutral-primary text-lg font-semibold">
                  Contact samenvoegen
                </h3>
                <p className="text-sm text-neutral-600">
                  Kies welk contact je wilt behouden
                </p>
              </div>
            </div>

            {/* Search for contact to merge with */}
            <div className="mb-4">
              <p className="mb-2 text-sm font-medium text-neutral-700">
                Zoek contact om mee samen te voegen:
              </p>
              <button
                onClick={() => setIsContactPickerOpen(true)}
                className="w-full rounded-lg border border-neutral-200 p-3 text-left transition-colors hover:border-neutral-300"
              >
                {mergeTargetContact ? (
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={mergeTargetDetails?.picture ?? undefined}
                      sizing="h-8 w-8"
                    />
                    <div>
                      <p className="font-medium">{mergeTargetContact.name}</p>
                      <p className="text-xs text-neutral-500">
                        {mergeTargetDetails?.email ||
                          mergeTargetContact.metadata1}
                      </p>
                    </div>
                  </div>
                ) : (
                  <span className="text-neutral-500">
                    Klik om een contact te selecteren...
                  </span>
                )}
              </button>
            </div>

            {/* Contact Picker Modal */}
            <UniversalPickerModal
              isOpen={isContactPickerOpen}
              onClose={() => setIsContactPickerOpen(false)}
              mode="reporter"
              title="Selecteer contact om mee samen te voegen"
              onConfirm={(selectedContact) => {
                if (
                  selectedContact?.id?.toString() !== contact?.id?.toString()
                ) {
                  setMergeTargetContact({
                    resourceId: selectedContact.id.toString(),
                    name: `${selectedContact.firstName} ${selectedContact.lastName}`,
                    metadata1:
                      selectedContact.phone_mobile || selectedContact.email,
                  });
                  fetchMergeTargetDetails(selectedContact.id.toString());
                  setKeepCurrentContact(false);
                } else {
                  toast.error(
                    "Je kunt een contact niet met zichzelf samenvoegen",
                  );
                }
                setIsContactPickerOpen(false);
              }}
              config={{
                searchPlaceholder: "Zoek op naam, e-mail of telefoon...",
                excludeId: contact?.id,
              }}
            />

            {/* Contact selection - choose which to keep */}
            {mergeTargetContact && mergeTargetDetails && (
              <div className="mb-4">
                <p className="mb-3 text-sm font-medium text-neutral-700">
                  Welk contact wil je behouden?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Current contact option */}
                  <button
                    onClick={() => setKeepCurrentContact(true)}
                    className={`rounded-lg border-2 p-4 text-left transition-all ${
                      keepCurrentContact
                        ? "border-moss-primary bg-moss-primary/5"
                        : "border-neutral-200 hover:border-neutral-300"
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span
                        className={`text-xs font-medium uppercase ${keepCurrentContact ? "text-moss-primary" : "text-neutral-500"}`}
                      >
                        {keepCurrentContact ? "✓ Behouden" : "Huidig contact"}
                      </span>
                      {!keepCurrentContact && (
                        <span className="text-brick-primary text-xs">
                          Wordt verwijderd
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Avatar
                        src={contact?.picture ?? undefined}
                        sizing="h-10 w-10"
                      />
                      <div>
                        <p className="font-medium">
                          {contact?.firstName} {contact?.lastName}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {contact?.email || "Geen e-mail"}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {contact?.phone_mobile || "Geen telefoon"}
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Other contact option */}
                  <button
                    onClick={() => setKeepCurrentContact(false)}
                    className={`rounded-lg border-2 p-4 text-left transition-all ${
                      !keepCurrentContact
                        ? "border-moss-primary bg-moss-primary/5"
                        : "border-neutral-200 hover:border-neutral-300"
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span
                        className={`text-xs font-medium uppercase ${!keepCurrentContact ? "text-moss-primary" : "text-neutral-500"}`}
                      >
                        {!keepCurrentContact ? "✓ Behouden" : "Ander contact"}
                      </span>
                      {keepCurrentContact && (
                        <span className="text-brick-primary text-xs">
                          Wordt verwijderd
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Avatar
                        src={mergeTargetDetails?.picture ?? undefined}
                        sizing="h-10 w-10"
                      />
                      <div>
                        <p className="font-medium">
                          {mergeTargetDetails?.firstName}{" "}
                          {mergeTargetDetails?.lastName}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {mergeTargetDetails?.email || "Geen e-mail"}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {mergeTargetDetails?.phone_mobile || "Geen telefoon"}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Field Selection when both contacts have conflicting values */}
            {mergeTargetDetails && contact && (
              <div className="mb-4">
                {/* Only show if there are conflicting fields */}
                {((contact.email &&
                  mergeTargetDetails.email &&
                  contact.email !== mergeTargetDetails.email) ||
                  (contact.phone_mobile &&
                    mergeTargetDetails.phone_mobile &&
                    contact.phone_mobile !== mergeTargetDetails.phone_mobile) ||
                  (contact.phone_direct &&
                    mergeTargetDetails.phone_direct &&
                    contact.phone_direct !==
                      mergeTargetDetails.phone_direct)) && (
                  <>
                    <p className="mb-3 text-sm font-medium text-neutral-700">
                      Beide contacten hebben verschillende gegevens. Welke wil
                      je behouden?
                    </p>
                    <div className="space-y-3">
                      {/* Email selection */}
                      {contact.email &&
                        mergeTargetDetails.email &&
                        contact.email !== mergeTargetDetails.email && (
                          <div className="rounded-lg border border-neutral-200 p-3">
                            <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                              E-mail
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  setFieldSelections((prev) => ({
                                    ...prev,
                                    email: "source",
                                  }))
                                }
                                className={`flex-1 rounded-lg border p-2 text-left text-sm transition-colors ${
                                  fieldSelections.email === "source"
                                    ? "border-moss-primary bg-moss-primary/10 text-moss-primary"
                                    : "border-neutral-200 hover:border-neutral-300"
                                }`}
                              >
                                <span className="text-xs text-neutral-500">
                                  {contact.firstName}:
                                </span>
                                <br />
                                {contact.email}
                              </button>
                              <button
                                onClick={() =>
                                  setFieldSelections((prev) => ({
                                    ...prev,
                                    email: "target",
                                  }))
                                }
                                className={`flex-1 rounded-lg border p-2 text-left text-sm transition-colors ${
                                  fieldSelections.email === "target"
                                    ? "border-moss-primary bg-moss-primary/10 text-moss-primary"
                                    : "border-neutral-200 hover:border-neutral-300"
                                }`}
                              >
                                <span className="text-xs text-neutral-500">
                                  {mergeTargetDetails.firstName}:
                                </span>
                                <br />
                                {mergeTargetDetails.email}
                              </button>
                            </div>
                          </div>
                        )}

                      {/* Phone mobile selection */}
                      {contact.phone_mobile &&
                        mergeTargetDetails.phone_mobile &&
                        contact.phone_mobile !==
                          mergeTargetDetails.phone_mobile && (
                          <div className="rounded-lg border border-neutral-200 p-3">
                            <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                              Mobiel
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  setFieldSelections((prev) => ({
                                    ...prev,
                                    phone_mobile: "source",
                                  }))
                                }
                                className={`flex-1 rounded-lg border p-2 text-left text-sm transition-colors ${
                                  fieldSelections.phone_mobile === "source"
                                    ? "border-moss-primary bg-moss-primary/10 text-moss-primary"
                                    : "border-neutral-200 hover:border-neutral-300"
                                }`}
                              >
                                <span className="text-xs text-neutral-500">
                                  {contact.firstName}:
                                </span>
                                <br />
                                {contact.phone_mobile}
                              </button>
                              <button
                                onClick={() =>
                                  setFieldSelections((prev) => ({
                                    ...prev,
                                    phone_mobile: "target",
                                  }))
                                }
                                className={`flex-1 rounded-lg border p-2 text-left text-sm transition-colors ${
                                  fieldSelections.phone_mobile === "target"
                                    ? "border-moss-primary bg-moss-primary/10 text-moss-primary"
                                    : "border-neutral-200 hover:border-neutral-300"
                                }`}
                              >
                                <span className="text-xs text-neutral-500">
                                  {mergeTargetDetails.firstName}:
                                </span>
                                <br />
                                {mergeTargetDetails.phone_mobile}
                              </button>
                            </div>
                          </div>
                        )}

                      {/* Phone direct selection */}
                      {contact.phone_direct &&
                        mergeTargetDetails.phone_direct &&
                        contact.phone_direct !==
                          mergeTargetDetails.phone_direct && (
                          <div className="rounded-lg border border-neutral-200 p-3">
                            <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
                              Telefoon direct
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  setFieldSelections((prev) => ({
                                    ...prev,
                                    phone_direct: "source",
                                  }))
                                }
                                className={`flex-1 rounded-lg border p-2 text-left text-sm transition-colors ${
                                  fieldSelections.phone_direct === "source"
                                    ? "border-moss-primary bg-moss-primary/10 text-moss-primary"
                                    : "border-neutral-200 hover:border-neutral-300"
                                }`}
                              >
                                <span className="text-xs text-neutral-500">
                                  {contact.firstName}:
                                </span>
                                <br />
                                {contact.phone_direct}
                              </button>
                              <button
                                onClick={() =>
                                  setFieldSelections((prev) => ({
                                    ...prev,
                                    phone_direct: "target",
                                  }))
                                }
                                className={`flex-1 rounded-lg border p-2 text-left text-sm transition-colors ${
                                  fieldSelections.phone_direct === "target"
                                    ? "border-moss-primary bg-moss-primary/10 text-moss-primary"
                                    : "border-neutral-200 hover:border-neutral-300"
                                }`}
                              >
                                <span className="text-xs text-neutral-500">
                                  {mergeTargetDetails.firstName}:
                                </span>
                                <br />
                                {mergeTargetDetails.phone_direct}
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Show relations that will be transferred */}
            {mergeTargetContact && mergeTargetDetails && (
              <>
                {/* Determine which relations to show based on which contact is being deleted */}
                {(() => {
                  const sourceRelations = keepCurrentContact
                    ? mergeTargetRelations
                    : contactRelations;
                  const sourceName = keepCurrentContact
                    ? mergeTargetContact.name
                    : `${contact?.firstName} ${contact?.lastName}`;
                  const hasRelations =
                    sourceRelations &&
                    (sourceRelations.relations.helpdeskTickets.length > 0 ||
                      sourceRelations.relations.contractContacts.length > 0 ||
                      sourceRelations.relations.buildingContacts.length > 0 ||
                      sourceRelations.relations.residents.length > 0 ||
                      sourceRelations.relations.scribbles.length > 0);

                  if (!hasRelations) return null;

                  return (
                    <div className="mb-4">
                      <p className="mb-2 text-sm font-medium text-neutral-700">
                        Gekoppelde gegevens van{" "}
                        <span className="text-brick-primary">{sourceName}</span>{" "}
                        die worden overgezet:
                      </p>
                      <div className="max-h-48 space-y-2 overflow-y-auto">
                        {sourceRelations.relations.helpdeskTickets.length >
                          0 && (
                          <div className="border-moss-primary/30 bg-moss-primary/5 rounded-lg border p-3">
                            <p className="text-moss-primary mb-2 text-xs font-medium uppercase">
                              Helpdesk Tickets (
                              {sourceRelations.relations.helpdeskTickets.length}
                              )
                            </p>
                            <div className="space-y-1">
                              {sourceRelations.relations.helpdeskTickets
                                .slice(0, 3)
                                .map((ticket) => (
                                  <div
                                    key={ticket.id}
                                    className="flex items-center justify-between text-sm"
                                  >
                                    <span className="text-neutral-700">
                                      #{ticket.id} - {ticket.title}
                                    </span>
                                    <span className="text-xs text-neutral-500">
                                      {ticket.status}
                                    </span>
                                  </div>
                                ))}
                              {sourceRelations.relations.helpdeskTickets
                                .length > 3 && (
                                <p className="text-xs text-neutral-500">
                                  ... en{" "}
                                  {sourceRelations.relations.helpdeskTickets
                                    .length - 3}{" "}
                                  meer
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {sourceRelations.relations.contractContacts.length >
                          0 && (
                          <div className="border-moss-primary/30 bg-moss-primary/5 rounded-lg border p-3">
                            <p className="text-moss-primary mb-2 text-xs font-medium uppercase">
                              Contracten (
                              {
                                sourceRelations.relations.contractContacts
                                  .length
                              }
                              )
                            </p>
                            <div className="space-y-1">
                              {sourceRelations.relations.contractContacts
                                .slice(0, 3)
                                .map((contract) => (
                                  <div
                                    key={contract.id}
                                    className="text-sm text-neutral-700"
                                  >
                                    Contract #{contract.contractId}
                                  </div>
                                ))}
                              {sourceRelations.relations.contractContacts
                                .length > 3 && (
                                <p className="text-xs text-neutral-500">
                                  ... en{" "}
                                  {sourceRelations.relations.contractContacts
                                    .length - 3}{" "}
                                  meer
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {sourceRelations.relations.buildingContacts.length >
                          0 && (
                          <div className="border-moss-primary/30 bg-moss-primary/5 rounded-lg border p-3">
                            <p className="text-moss-primary mb-2 text-xs font-medium uppercase">
                              Gebouw koppelingen (
                              {
                                sourceRelations.relations.buildingContacts
                                  .length
                              }
                              )
                            </p>
                            <div className="space-y-1">
                              {sourceRelations.relations.buildingContacts
                                .slice(0, 3)
                                .map((bc) => (
                                  <div
                                    key={bc.id}
                                    className="flex items-center justify-between text-sm"
                                  >
                                    <span className="text-neutral-700">
                                      {bc.buildingId}
                                    </span>
                                    <span className="text-xs text-neutral-500">
                                      {bc.type}
                                    </span>
                                  </div>
                                ))}
                              {sourceRelations.relations.buildingContacts
                                .length > 3 && (
                                <p className="text-xs text-neutral-500">
                                  ... en{" "}
                                  {sourceRelations.relations.buildingContacts
                                    .length - 3}{" "}
                                  meer
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {sourceRelations.relations.residents.length > 0 && (
                          <div className="border-moss-primary/30 bg-moss-primary/5 rounded-lg border p-3">
                            <p className="text-moss-primary mb-2 text-xs font-medium uppercase">
                              Bewoner koppelingen (
                              {sourceRelations.relations.residents.length})
                            </p>
                          </div>
                        )}

                        {sourceRelations.relations.scribbles.length > 0 && (
                          <div className="border-moss-primary/30 bg-moss-primary/5 rounded-lg border p-3">
                            <p className="text-moss-primary mb-2 text-xs font-medium uppercase">
                              Notities (
                              {sourceRelations.relations.scribbles.length})
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {mergeTargetContact && (
              <div className="mb-6 rounded-lg bg-neutral-50 p-4">
                <p className="text-xs text-neutral-600">
                  <strong>Let op:</strong>{" "}
                  {keepCurrentContact ? (
                    <>
                      <span className="text-brick-primary font-medium">
                        {mergeTargetContact.name}
                      </span>{" "}
                      wordt verwijderd. Alle gekoppelde gegevens worden
                      overgezet naar{" "}
                      <span className="text-moss-primary font-medium">
                        {contact?.firstName} {contact?.lastName}
                      </span>
                      .
                    </>
                  ) : (
                    <>
                      <span className="text-brick-primary font-medium">
                        {contact?.firstName} {contact?.lastName}
                      </span>{" "}
                      wordt verwijderd. Alle gekoppelde gegevens worden
                      overgezet naar{" "}
                      <span className="text-moss-primary font-medium">
                        {mergeTargetContact.name}
                      </span>
                      .
                    </>
                  )}
                </p>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setIsMergeModalOpen(false);
                  setMergeTargetContact(null);
                  setMergeTargetDetails(null);
                  setMergeTargetRelations(null);
                  setFieldSelections({});
                  setKeepCurrentContact(false);
                }}
                className="rounded-lg border border-neutral-200 px-4 py-2 text-neutral-600 transition-colors hover:bg-neutral-50"
                disabled={isMerging}
              >
                Annuleren
              </button>
              <button
                onClick={handleMergeContacts}
                disabled={isMerging || !mergeTargetContact}
                className="bg-moss-primary hover:bg-moss-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-white transition-colors disabled:opacity-50"
              >
                {isMerging ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  <GitMerge size={16} />
                )}
                {isMerging ? "Samenvoegen..." : "Samenvoegen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Preview Modal */}
      {isPreviewModalOpen && previewContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar
                  src={previewContact.picture ?? undefined}
                  sizing="h-12 w-12"
                />
                <div>
                  <h3 className="text-neutral-primary text-lg font-semibold">
                    {previewContact.firstName} {previewContact.lastName}
                  </h3>
                  {previewContact.company?.name && (
                    <p className="text-sm text-neutral-500">
                      {previewContact.company.name}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setIsPreviewModalOpen(false);
                  setPreviewContact(null);
                }}
                className="rounded-full p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              {/* Contact info */}
              {previewContact.email && (
                <div className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3">
                  <Email className="text-neutral-400" />
                  <div>
                    <p className="text-xs text-neutral-500">E-mail</p>
                    <p className="text-sm font-medium">
                      {previewContact.email}
                    </p>
                  </div>
                </div>
              )}

              {previewContact.phone_mobile && (
                <div className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3">
                  <Phone className="text-neutral-400" />
                  <div>
                    <p className="text-xs text-neutral-500">Mobiel</p>
                    <p className="text-sm font-medium">
                      {previewContact.phone_mobile}
                    </p>
                  </div>
                </div>
              )}

              {previewContact.phone_direct && (
                <div className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3">
                  <Phone className="text-neutral-400" />
                  <div>
                    <p className="text-xs text-neutral-500">Direct</p>
                    <p className="text-sm font-medium">
                      {previewContact.phone_direct}
                    </p>
                  </div>
                </div>
              )}

              {previewContact.jobTitle && (
                <div className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3">
                  <Building className="text-neutral-400" />
                  <div>
                    <p className="text-xs text-neutral-500">Functie</p>
                    <p className="text-sm font-medium">
                      {previewContact.jobTitle}
                    </p>
                  </div>
                </div>
              )}

              {previewContact.address?.formattedAddress && (
                <div className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3">
                  <LocationAlt className="text-neutral-400" />
                  <div>
                    <p className="text-xs text-neutral-500">Adres</p>
                    <p className="text-sm font-medium">
                      {previewContact.address.formattedAddress}
                    </p>
                  </div>
                </div>
              )}

              {previewContact.tags && previewContact.tags.length > 0 && (
                <div className="rounded-lg bg-neutral-50 p-3">
                  <p className="mb-2 text-xs text-neutral-500">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {previewContact.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="border-moss-primary text-moss-primary rounded-full border bg-white px-2 py-0.5 text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <Link
                to={`/contact/${previewContact.id}`}
                onClick={() => {
                  setIsPreviewModalOpen(false);
                  setPreviewContact(null);
                  setDuplicateWarning(null);
                }}
                className="text-moss-primary hover:text-moss-primary/80 text-sm underline"
              >
                Ga naar contact
              </Link>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setIsPreviewModalOpen(false);
                    setPreviewContact(null);
                  }}
                  className="rounded-lg border border-neutral-200 px-4 py-2 text-neutral-600 transition-colors hover:bg-neutral-50"
                >
                  Sluiten
                </button>
                {/* Only show merge button when editing an existing contact */}
                {id && (
                  <button
                    onClick={() => {
                      setMergeTargetContact({
                        resourceId: previewContact.id!.toString(),
                        name: `${previewContact.firstName} ${previewContact.lastName}`,
                        metadata1: previewContact.phone_mobile,
                      });
                      fetchMergeTargetDetails(previewContact.id!.toString());
                      setIsPreviewModalOpen(false);
                      setPreviewContact(null);
                      setIsMergeModalOpen(true);
                      setDuplicateWarning(null);
                    }}
                    className="bg-moss-primary hover:bg-moss-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 text-white transition-colors"
                  >
                    <GitMerge size={16} />
                    Samenvoegen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetails;
